import React from 'react';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import withProps from 'recompose/withProps';
import * as ResourceApi from 'api/resources';
import DocumentTitle from 'react-document-title';
import setDisplayName from 'recompose/setDisplayName';
import ItemExplorer from 'components/common/ItemExplorer';
import SecretSonicSpinner from 'components/common/SecretSonicSpinner';
import {createResourceSortsSelector, createResourceSortOptionsSelector} from 'viewOptions/selectors';
import {removeAllFiltersForResourceAction, toggleSortForResourceAction} from 'viewOptions/actions';
import {createResourcePageSelector, createResourcePageItemsSelector} from 'resources/selectors';
import PrizeChallengesModal from 'components/PrizeChallengesModal/PrizeChallengesModal';
import {openPrizeChallengesModal} from 'components/PrizeChallengesModal/actions';
import WinnerDetailModal from 'components/WinnerDetailModal/WinnerDetailModal';
import {openWinnerDetailModal} from 'components/WinnerDetailModal/actions';
import {updatePageNumberForResourceAction} from 'resources/actions';
import EditWinnerStoryModal from './EditWinnerStoryModal';
import StatusModal from 'components/common/StatusModal';
import * as WinnersActions from './actions';
import {
  EmptyFunc,
  SortDirection,
  ItemExplorerViewOptions,
  pageCount,
  prependRecentIfFirstPage
} from 'utils/DBUtils';
import EditWinnerNotesModal from "./EditWinnerNotesModal";
import {isNoU} from "../../utils/DBUtils";

const getResourcePage = createResourcePageSelector(ResourceApi.Types.PrizeEntry);
const getPageItems = createResourcePageItemsSelector(ResourceApi.Types.PrizeEntry);
const getSorts = createResourceSortsSelector(ResourceApi.Types.PrizeEntry);
const getSortOptions = createResourceSortOptionsSelector(ResourceApi.Types.PrizeEntry);

const mapStateToProps = state => {
  const {
    fetchingWinners,
    currentExpandedRow,
    searchResults,
    dateOrderedPendingSaves,
    dateOrderedSaveSuccesses,
    dateOrderedSaveErrors,
    isEditWinnerStoryModalOpen,
    winnerStorySaveError,
    notesSaveError,
    savingNotes,
    editingWinnerNotes,
    savingWinnerStory,
    isFeaturedErrorModalOpen,
    featuredErrorModalLang,
    featuredWinnerPendingSaves,
    featuredWinnerSaveSuccesses,
    featuredWinnerSaveErrors
  } = state.listWinners;

  const resourcePage = getResourcePage(state.resources);
  const pageSize = resourcePage.count;
  const currentPage = resourcePage.page;
  const totalWinners = resourcePage.total;

  const isWinnerDetailModalOpen = state.winnerDetail.isOpen;

  return {
    fetchingWinners,
    currentExpandedRow,
    searchResults,
    isWinnerDetailModalOpen,
    dateOrderedPendingSaves,
    dateOrderedSaveSuccesses,
    dateOrderedSaveErrors,
    isEditWinnerStoryModalOpen,
    winnerStorySaveError,
    savingWinnerStory,
    isFeaturedErrorModalOpen,
    featuredErrorModalLang,
    featuredWinnerPendingSaves,
    featuredWinnerSaveSuccesses,
    featuredWinnerSaveErrors,
    isPrizeChallengesModalOpen: state.prizeChallengesModal.isOpen,
    winners: prependRecentIfFirstPage(currentPage, pageSize, [], getPageItems(state.resources)),
    currentPage,
    pageSize,
    totalWinners,
    savingNotes,
    isEditWinnerNotesModalOpen : !isNoU(editingWinnerNotes),
    notesSaveError,
    sortOptions: getSortOptions(state),
    getSortDirectionForField: field => getSorts(state)[field] || SortDirection.Unsorted
  };
};

const mapDispatchToProps = {
  openWinnerDetailModal,
  openPrizeChallengesModal,
  expandRow: WinnersActions.expandRow,
  collapseRow: WinnersActions.collapseRow,
  saveDateOrdered: WinnersActions.submitSaveDateOrderedAction,
  saveWinnerStory: WinnersActions.saveWinnerStoryAction,
  saveNotes: WinnersActions.saveNotesAction,
  closeEditWinnerNotesModal: WinnersActions.endEditNotesAction,
  startEditNotes: WinnersActions.startEditNotesAction,
  openEditWinnerStoryModal: WinnersActions.openEditWinnerStoryModalAction,
  closeEditWinnerStoryModal: WinnersActions.closeEditWinnerStoryModalAction,
  toggleFeaturedWinner: WinnersActions.submitToggleFeaturedWinnerAction,
  closeFeaturedErrorModal: WinnersActions.closeFeaturedErrorModalAction,
  onPageSelected: page => updatePageNumberForResourceAction(ResourceApi.Types.PrizeEntry, page, {winnersOnly: true}),
  onNextClicked: currentPage => updatePageNumberForResourceAction(ResourceApi.Types.PrizeEntry, currentPage+1, {winnersOnly: true}),
  onPreviousClicked: currentPage => updatePageNumberForResourceAction(ResourceApi.Types.PrizeEntry, currentPage-1, {winnersOnly: true}),
  selectSort: field => toggleSortForResourceAction(ResourceApi.Types.PrizeEntry, field, {winnersOnly: true})
};

const ListWinnersTable = compose(
  withProps(props => ({
    showView: ItemExplorerViewOptions.List,
    itemsOnly: true,
    filterBarProps: {
      hidden: true
    },
    pagingBarProps: {
      count: pageCount(props.totalWinners, props.pageSize),
      total: props.totalWinners,
      currentPage: props.currentPage,
      maxPages: 10,
      onPageSelected: props.onPageSelected,
      onNextClicked: props.onNextClicked,
      onPreviousClicked: props.onPreviousClicked
    },
    tableProps: {
      scrolling: true,
      items: (props.searchResults || props.winners).map(winner => {
        if (!winner.prize) {
          console.warn(`PrizeEntry with id '${winner.id}' has no 'prize' property.`);
        }

        // If we have fulfillment info use that as the first/last name instead of what's already on the winner record
        if (winner.fulfillmentInfo) {
          winner.firstName = winner.fulfillmentInfo.firstName;
          winner.lastName = winner.fulfillmentInfo.lastName;
        }

        return winner;
      }),
      onRowClicked: EmptyFunc,
      animatedSubRows: true,
      showSubRows: winner => winner.id === props.currentExpandedRow,

      // HACK: using ItemTable subrows requires the parent row to be counted as a subrow,,
      //       so we pass null in its place and tell ItemTable to only render truthy values.
      getSubRows: winner => [null, winner],
      showSubRow: Boolean,

      getSortDirectionForField: props.getSortDirectionForField,
      onSortClick: col => {
        if (col.fieldName) {
          props.selectSort(col.fieldName);
        }
      },
      colDetails: props.winnersTableConfig.columns.map(col => col(props))
    }
  })),
  setDisplayName('ListWinnersTable')
)(ItemExplorer);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  setDisplayName('ListWinnersPage')
)(props => (
  <DocumentTitle title={props.winnersTableConfig.documentTitle}>
    <div className={`list-winners-page ${props.winnersTableConfig.topLevelClassName}`}>
      {props.fetchingWinners ? <SecretSonicSpinner messageKey={props.winnersTableConfig.spinnerMessage} /> : (
        <React.Fragment>
          <ListWinnersTable {...props} />
          {props.isEditWinnerStoryModalOpen && (
            <EditWinnerStoryModal
              handleSubmit={props.saveWinnerStory}
              isInFlight={props.savingWinnerStory}
              isOpen={props.isEditWinnerStoryModalOpen}
              hasSaveError={props.winnerStorySaveError}
              handleClose={props.closeEditWinnerStoryModal} />
          )}
          {props.isEditWinnerNotesModalOpen && (
              <EditWinnerNotesModal
                  handleSubmit={props.saveNotes}
                  isInFlight={props.savingNotes}
                  isOpen={props.isEditWinnerNotesModalOpen}
                  hasSaveError={props.notesSaveError}
                  handleClose={props.closeEditWinnerNotesModal} />
          )}
          {props.isFeaturedErrorModalOpen && (
            <StatusModal
              isOpen={props.isFeaturedErrorModalOpen}
              onClose={props.closeFeaturedErrorModal}
              className='featured-error-modal'
              size='tiny'
              success={false}
              errorHeaderKey='error'
              errorMessageKey={`winnersList.error.makeFeatured.${props.featuredErrorModalLang}`} />
          )}
          {props.isWinnerDetailModalOpen && <WinnerDetailModal />}
          {props.isPrizeChallengesModalOpen && <PrizeChallengesModal />}
        </React.Fragment>
      )}
    </div>
  </DocumentTitle>
));



// WEBPACK FOOTER //
// ./public/pages/ListWinners/ListWinnersPage.jsx